/**
 * VenExpService
 * 
 * This service handles all vendor expense-related operations in the application.
 * It provides methods for managing vendor expenses, including creation, retrieval,
 * updating, and deletion of vendor expense entries. The service supports operations
 * for both individual matters and system-wide vendor expense management.
 * 
 * @class
 */

import HttpService from './HttpService';
import {
  pathVenExpenses,
  pathVenExpensesByMatter,
  pathVenExpenseById,
} from '@frontend/__settings__/septSoftwarePaths';

const http = new HttpService();

class VenExpService {
    /**
     * Retrieves all vendor expenses from the system
     * 
     * @returns {Promise<Array>} Array of vendor expense objects
     * @throws {Error} If the API request fails
     */
    static async getAllVenExpenses() {
      return http.get(pathVenExpenses);
    }

    /**
     * Retrieves vendor expenses for a specific matter
     * 
     * @param {string} matterId - ID of the matter
     * @returns {Promise<Array>} Array of vendor expenses for the matter
     * @throws {Error} If the API request fails
     */
    static async getVenExpensesByMatter(matterId: string) {
      return http.get(pathVenExpensesByMatter(matterId));
    }

    /**
     * Adds a new vendor expense
     * 
     * @param {Object} expenseData - Vendor expense data
     * @param {string} expenseData.matterId - ID of the matter
     * @param {string} expenseData.name - Name of the expense
     * @param {number} expenseData.amount - Amount of the expense
     * @param {string} [expenseData.description] - Description of the expense
     * 
     * @returns {Promise<Object>} Created vendor expense object
     * @throws {Error} If the API request fails
     */
    static async addVenExpense(expenseData: any) {
      return http.post(pathVenExpenses, expenseData);
    }

    /**
     * Updates an existing vendor expense
     * 
     * @param {string} venId - ID of the vendor expense
     * @param {Object} expenseData - Updated vendor expense data
     * @returns {Promise<Object>} Updated vendor expense object
     * @throws {Error} If the API request fails
     */
    static async updateVenExpense(venId: string, expenseData: any) {
      return http.put(pathVenExpenseById(venId), expenseData);
    }
    
    /**
     * Deletes a vendor expense
     * 
     * @param {string} venId - ID of the vendor expense to delete
     * @returns {Promise<Object>} Response indicating success or failure
     * @throws {Error} If the API request fails
     */
    static async deleteVenExpense(venId: string) {
      return http.delete(pathVenExpenseById(venId));
    }
}

export default VenExpService;
